import { ParamMap, Params, Router } from '@angular/router';

/**
 * v3 group-order shell is served at `/store` with the order id in the `id` query param
 * (legacy entry URLs used `/group-order?groupOrderId=` and are redirected in {@link v3LegacyGroupOrderPathRedirectGuard}).
 */
export const V3_GROUP_ORDER_BASE_PATH = '/store' as const;

/** Store preview routes that embed checkout payment UI (skip order-expired redirect). */
export function isV3StorePaymentPreviewPath(path: string): boolean {
  const normalized = path.split('?')[0].split('#')[0];
  return (
    normalized === `${V3_GROUP_ORDER_BASE_PATH}/paymentpage` ||
    normalized === `${V3_GROUP_ORDER_BASE_PATH}/failed-payment` ||
    normalized === `${V3_GROUP_ORDER_BASE_PATH}/failed-payment-with-reason` ||
    normalized === `${V3_GROUP_ORDER_BASE_PATH}/success-page`
  );
}

/**
 * Resolves the numeric group order id from the storefront URL: prefers `id`, then legacy `groupOrderId`.
 */
export function v3GroupOrderIdFromRouteParams(
  params: Params | { get: (k: string) => string | null } | null | undefined
): string | null {
  if (params == null) {
    return null;
  }
  const get = (k: string) => {
    if (typeof (params as ParamMap).get === 'function') {
      return (params as ParamMap).get(k);
    }
    const p = (params as Params)[k];
    if (p === undefined || p === null) {
      return null;
    }
    return String(p);
  };
  return get('id') || get('groupOrderId');
}

/**
 * Parse a positive integer group order id from `id` / `groupOrderId` query params, or `null` if missing/invalid.
 */
export function v3ParsedGroupOrderIdFromRouteParams(
  params: Params | { get: (k: string) => string | null } | null | undefined
): number | null {
  const raw = v3GroupOrderIdFromRouteParams(params);
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return null;
  }
  return n;
}

/**
 * {@link v3GroupOrderIdQueryParam} and optional co-query keys for `router.navigate` / `queryParams`.
 */
export function v3GroupOrderIdQueryParam(
  groupOrderId: number | string,
  extra?: Record<string, string | number | boolean | undefined | null>
): Record<string, string> {
  const out: Record<string, string> = { id: String(groupOrderId) };
  if (!extra) {
    return out;
  }
  for (const [k, v] of Object.entries(extra)) {
    if (v === undefined || v === null) {
      continue;
    }
    out[k] = String(v);
  }
  return out;
}

/**
 * sessionStorage key for the last storefront product opened from PLP / viewed on PDP,
 * used when `/store?plpProductId=` is absent (browser history, stripped URL, etc.).
 */
export function storefrontPlpReturnProductSessionKey(groupOrderId: number): string {
  return `fp_v3_plp_return_pid_${groupOrderId}`;
}

/**
 * Navigate to global 404. Pass `orderId` when the shopper had a storefront group-order id in context
 * so {@link PageNotFoundComponent} can show store chrome (timer, cart) and a return-to-store CTA.
 */
export function navigateToV3PageNotFound(router: Router, orderId?: number | null): Promise<boolean> {
  const idNum = orderId != null ? Number(orderId) : NaN;
  const hasId = Number.isFinite(idNum) && idNum > 0 && Number.isInteger(idNum);
  return router.navigate(['/page-not-found'], hasId ? { queryParams: v3GroupOrderIdQueryParam(idNum) } : undefined);
}
